/**
 * ProspectsKanban — primary landing view for the Prospects feature.
 *
 * Horizontal-scrolling kanban with one column per active stage. Each card
 * shows the applicant name, time-in-stage, status badge, marketplace-install
 * and paid-fee flags, and affiliate display name when attributed.
 *
 * Drag-and-drop moves a prospect to a different stage column. Confirmation
 * modal pops up with an optional note field. The transition is audited
 * regardless of whether a note is provided.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, RefreshCw, CheckCircle2 } from 'lucide-react';
import { getKanban, transitionProspectStage } from './api';
import type { KanbanColumn, KanbanCard, StageStatus } from './types';

interface Props {
  onOpenProspect: (prospectId: string) => void;
}

interface DragState {
  cardId: string;
  fromStageId: string;
}

interface ConfirmState {
  card: KanbanCard;
  fromStageId: string;
  toStageId: string;
  toStageName: string;
}

export default function ProspectsKanban({ onOpenProspect }: Props) {
  const [columns, setColumns] = useState<KanbanColumn[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await getKanban();
    if (!res.success || !res.data) {
      setError(res.error || 'Failed to load pipeline.');
      setColumns([]);
    } else {
      setColumns(res.data.stages);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleDragStart = (e: React.DragEvent, card: KanbanCard, fromStageId: string) => {
    setDrag({ cardId: card.id, fromStageId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, toStageId: string, toStageName: string) => {
    e.preventDefault();
    if (!drag || !columns) return;
    if (drag.fromStageId === toStageId) {
      setDrag(null);
      return;
    }
    // Find the card and stage display
    const fromCol = columns.find((c) => c.id === drag.fromStageId);
    const card = fromCol?.prospects.find((p) => p.id === drag.cardId);
    if (!card) {
      setDrag(null);
      return;
    }
    setConfirm({
      card,
      fromStageId: drag.fromStageId,
      toStageId,
      toStageName,
    });
    setNote('');
    setDrag(null);
  };

  const handleConfirmMove = async () => {
    if (!confirm) return;
    setSubmitting(true);
    // Default the new sub-status to 'awaiting_admin' (most common case for an
    // admin-initiated move). Admin can change it later via the detail page.
    const toStatus: StageStatus = 'awaiting_admin';
    const res = await transitionProspectStage(
      confirm.card.id,
      confirm.toStageId,
      toStatus,
      note.trim() || undefined
    );
    setSubmitting(false);
    if (!res.success) {
      alert(res.error || 'Failed to move prospect.');
      return;
    }
    setConfirm(null);
    await load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Loading pipeline…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-red-800 font-medium">Couldn't load the pipeline</p>
          <p className="text-red-700 text-sm mt-1">{error}</p>
          <button
            onClick={load}
            className="mt-2 text-sm text-red-700 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!columns || columns.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No pipeline stages configured. Use Settings → Stages to create some.
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {columns.reduce((n, c) => n + c.prospects.length, 0)} active prospects across{' '}
          {columns.length} stages.{' '}
          <span className="text-gray-400">Drag a card between columns to move it.</span>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
          title="Refresh pipeline"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {columns.map((col) => (
            <KanbanColumnView
              key={col.id}
              column={col}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id, col.display_name)}
              onCardDragStart={handleDragStart}
              onCardClick={onOpenProspect}
            />
          ))}
        </div>
      </div>

      {confirm && (
        <MoveConfirmDialog
          confirm={confirm}
          note={note}
          onNoteChange={setNote}
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirmMove}
          submitting={submitting}
        />
      )}
    </>
  );
}

// =============================================================================
// Stage column
// =============================================================================

function KanbanColumnView({
  column,
  onDragOver,
  onDrop,
  onCardDragStart,
  onCardClick,
}: {
  column: KanbanColumn;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onCardDragStart: (e: React.DragEvent, card: KanbanCard, fromStageId: string) => void;
  onCardClick: (id: string) => void;
}) {
  const isEmpty = column.prospects.length === 0;
  const accent = column.is_terminal_good
    ? 'border-t-green-500'
    : column.is_terminal_bad
    ? 'border-t-red-500'
    : 'border-t-blue-500';

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`w-72 flex-shrink-0 bg-gray-50 rounded-lg border-t-4 ${accent} flex flex-col`}
      style={{ minHeight: 400 }}
    >
      <div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
        <span className="font-medium text-sm text-gray-900">{column.display_name}</span>
        <span className="text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-600">
          {column.prospects.length}
        </span>
      </div>
      <div className="flex-1 p-2 space-y-2">
        {isEmpty ? (
          <p className="text-xs text-gray-400 italic text-center py-6">(empty)</p>
        ) : (
          column.prospects.map((card) => (
            <KanbanCardView
              key={card.id}
              card={card}
              fromStageId={column.id}
              onDragStart={onCardDragStart}
              onClick={onCardClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Card
// =============================================================================

function KanbanCardView({
  card,
  fromStageId,
  onDragStart,
  onClick,
}: {
  card: KanbanCard;
  fromStageId: string;
  onDragStart: (e: React.DragEvent, card: KanbanCard, fromStageId: string) => void;
  onClick: (id: string) => void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card, fromStageId)}
      onClick={() => onClick(card.id)}
      className="bg-white border border-gray-200 rounded-md p-2.5 cursor-pointer hover:border-blue-400 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate">
            {card.applicant_first_name} {card.applicant_last_name}
          </p>
          <p className="text-xs text-gray-500 truncate">{card.applicant_email}</p>
        </div>
        <StatusBadge status={card.current_stage_status} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs text-gray-500">
        <span>{formatDuration(card.time_in_stage_seconds)}</span>
        <div className="flex items-center gap-1.5">
          {card.paid_fee && (
            <span title="Paid initial fee">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
            </span>
          )}
          {card.marketplace_installed && (
            <span title="Marketplace app installed">
              <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
            </span>
          )}
        </div>
      </div>
      {card.affiliate_display_name && (
        <div className="mt-1.5 text-xs text-gray-600 truncate">
          via <span className="text-gray-800 font-medium">{card.affiliate_display_name}</span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Status badge
// =============================================================================

function StatusBadge({ status }: { status: StageStatus }) {
  const config: Record<StageStatus, { label: string; classes: string }> = {
    awaiting_client: { label: 'Client', classes: 'bg-amber-100 text-amber-800' },
    awaiting_admin: { label: 'Us', classes: 'bg-blue-100 text-blue-800' },
    awaiting_external: { label: 'Review', classes: 'bg-purple-100 text-purple-800' },
    rejected_needs_fix: { label: 'Fix', classes: 'bg-red-100 text-red-800' },
    complete: { label: 'Done', classes: 'bg-green-100 text-green-800' },
  };
  const c = config[status] || config.awaiting_admin;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ${c.classes}`}
    >
      {c.label}
    </span>
  );
}

// =============================================================================
// Confirm move dialog
// =============================================================================

function MoveConfirmDialog({
  confirm,
  note,
  onNoteChange,
  onCancel,
  onConfirm,
  submitting,
}: {
  confirm: ConfirmState;
  note: string;
  onNoteChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Move to {confirm.toStageName}?
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {confirm.card.applicant_first_name} {confirm.card.applicant_last_name}
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Note <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          rows={3}
          placeholder="Why are you moving them?"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Moving…' : 'Move prospect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatDuration(seconds: number): string {
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m in stage`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h in stage`;
  const days = Math.floor(hours / 24);
  return `${days}d in stage`;
}
